import React, { useCallback, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { CRMAccountTile } from "../CRMAccountTile";
import { useVoiceAccount } from "../../hooks/useVoiceAccount";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as VoiceIcon } from "./assets/voice.svg";
import { ReactComponent as LightningIcon } from "./assets/lightning.svg";
import { ReactComponent as CallForwardingIcon } from "./assets/call-forwarding.svg";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import { ReactComponent as StarIcon } from "./assets/star.svg";
import { ReactComponent as PhoneIcon } from "./assets/phone.svg";

export const VoiceAccountCard: React.FC<{
  isClickable?: boolean;
}> = ({ isClickable }) => {
  const { t } = useTranslations();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const { account, isConnected, createAccount, isCreating } = useVoiceAccount();
  const { isFree, openSubscriptionDrawer } = useSubscriptionV2();
  const requiresPaidPlan = isFree && !isConnected;

  const handleClick = useCallback(async () => {
    if (!isClickable || isCreating) {
      return;
    }

    if (!isConnected) {
      if (requiresPaidPlan) {
        openSubscriptionDrawer();
        return;
      }

      await createAccount();
      return;
    }

    transitionTo({ name: "voiceAccount" });
  }, [
    createAccount,
    isClickable,
    isConnected,
    isCreating,
    requiresPaidPlan,
    openSubscriptionDrawer,
    transitionTo,
  ]);

  const features = useMemo(() => {
    if (isConnected) {
      return [
        { icon: <PhoneIcon />, label: account?.phone_num ?? "" },
        {
          icon: <PhoneIcon />,
          label: account?.metadata?.handover_phone ?? "",
        },
      ];
    }

    return [
      {
        icon: <LightningIcon />,
        label: t({
          id: "client-support-page.voice-tile-feature-ai-handled-calls",
          defaultMessage: "AI handled calls",
        }),
      },
      {
        icon: <CallForwardingIcon />,
        label: t({
          id: "client-support-page.voice-tile-feature-call-forwarding",
          defaultMessage: "Call forwarding",
        }),
      },
      {
        icon: <FileIcon />,
        label: t({
          id: "client-support-page.voice-tile-feature-calls-history-transcribed",
          defaultMessage: "Calls history transcribed",
        }),
      },
      {
        icon: <StarIcon />,
        label: t({
          id: "client-support-page.voice-tile-feature-custom-ai-instructions",
          defaultMessage: "Custom AI instructions",
        }),
      },
    ];
  }, [account?.metadata?.handover_phone, account?.phone_num, isConnected, t]);

  return (
    <CRMAccountTile
      title={t({
        id: "client-support-page.voice",
        defaultMessage: "Phone calls",
      })}
      isConnected={isConnected}
      isClickable={isClickable && !isCreating}
      onClick={handleClick}
      icon={<VoiceIcon />}
      features={features}
      status={isConnected ? "enabled" : undefined}
      connectLabel={
        requiresPaidPlan
          ? t({
              id: "client-support-page.upgrade-plan",
              defaultMessage: "Upgrade Plan",
            })
          : t({
              id: "client-support-page.enable",
              defaultMessage: "Enable",
            })
      }
      pricingTooltip={
        requiresPaidPlan
          ? t({
              id: "cx-ai-agents-page.phone-calls-paid-plan-tooltip",
              defaultMessage:
                "Available for Paid Plans only. Upgrade your Plan to get Phone Calls Feature ℹ️",
            })
          : undefined
      }
    />
  );
};
